import CrudSection from "../components/CrudSection";
import FeatureDashboard from "../../../components/base/FeatureDashboard";
import { StatusTextBadge } from "../../../components/base/StatusTextBadge";
import type { CrudColumn, CrudField } from "../types";

// ===========================================================================
// Reservas — bespoke sections.
// ===========================================================================

export function ReservasDashboard() {
  return (
    <FeatureDashboard noticeKey="nuvexAccess.mockNotice"
      title="Reservas"
      subtitle="Amenidades, disponibilidad y reglas"
      icon="ri-calendar-check-line"
      stats={[
        { label: "Reservas hoy", value: "26", icon: "ri-calendar-check-line", change: "+4", positive: true },
        { label: "Amenidades", value: "9", icon: "ri-community-line" },
        { label: "Ocupación promedio", value: "71%", icon: "ri-pie-chart-line", change: "+6%", positive: true },
        { label: "Por confirmar", value: "5", icon: "ri-time-line" },
      ]}
      chart={{
        title: "Reservas por día (semana)",
        data: [
          { name: "Lun", value: 12 }, { name: "Mar", value: 18 }, { name: "Mié", value: 15 },
          { name: "Jue", value: 22 }, { name: "Vie", value: 30 }, { name: "Sáb", value: 41 }, { name: "Dom", value: 26 },
        ],
      }}
      recent={{
        title: "Reservaciones recientes",
        items: [
          { primary: "Salón de eventos — Depto 402", secondary: "Sáb 18:00", badge: "Confirmada", tone: "ok" },
          { primary: "Cancha de pádel — Casa 12", secondary: "Hoy 19:00", badge: "Por confirmar", tone: "warn" },
          { primary: "Asador 2 — Depto 510", secondary: "Dom 14:00", badge: "Confirmada", tone: "ok" },
        ],
      }}
    />
  );
}

// --- Amenidades ------------------------------------------------------------
interface Amenidad extends Record<string, unknown> {
  id: string; nombre: string; tipo: string; capacidad: string; estado: string;
}
const amenidadColumns: CrudColumn<Amenidad>[] = [
  { key: "nombre", label: "Amenidad" },
  { key: "tipo", label: "Tipo" },
  { key: "capacidad", label: "Capacidad", align: "right" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const amenidadFields: CrudField[] = [
  { key: "nombre", label: "Nombre", required: true, placeholder: "Salón de eventos" },
  { key: "tipo", label: "Tipo", type: "select", defaultValue: "Salón", options: [
    { value: "Salón", label: "Salón" }, { value: "Cancha", label: "Cancha" }, { value: "Asador", label: "Asador" }, { value: "Alberca", label: "Alberca" },
  ] },
  { key: "capacidad", label: "Capacidad", type: "number", defaultValue: 0 },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Disponible", options: [
    { value: "Disponible", label: "Disponible" }, { value: "Mantenimiento", label: "Mantenimiento" },
  ] },
];
const amenidadSeed: Amenidad[] = [
  { id: "am1", nombre: "Salón de eventos", tipo: "Salón", capacidad: "80", estado: "Disponible" },
  { id: "am2", nombre: "Cancha de pádel", tipo: "Cancha", capacidad: "4", estado: "Disponible" },
  { id: "am3", nombre: "Asador 2", tipo: "Asador", capacidad: "12", estado: "Mantenimiento" },
];
export const AmenidadesSection = () => (
  <CrudSection<Amenidad> title="Amenidades" subtitle="Espacios reservables de la comunidad"
    icon="ri-community-line" columns={amenidadColumns} fields={amenidadFields} seed={amenidadSeed} searchKey="nombre" />
);

// --- Reservaciones ---------------------------------------------------------
interface Reservacion extends Record<string, unknown> {
  id: string; amenidad: string; solicitante: string; fecha: string; horario: string; estado: string;
}
const reservacionColumns: CrudColumn<Reservacion>[] = [
  { key: "amenidad", label: "Amenidad" },
  { key: "solicitante", label: "Solicitante" },
  { key: "fecha", label: "Fecha" },
  { key: "horario", label: "Horario" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const reservacionFields: CrudField[] = [
  { key: "amenidad", label: "Amenidad", type: "select", required: true, options: [
    { value: "Salón de eventos", label: "Salón de eventos" }, { value: "Cancha de pádel", label: "Cancha de pádel" }, { value: "Asador 2", label: "Asador 2" },
  ] },
  { key: "solicitante", label: "Solicitante", required: true, placeholder: "Depto 402" },
  { key: "fecha", label: "Fecha", type: "date", required: true },
  { key: "horario", label: "Horario", required: true, placeholder: "18:00–22:00" },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Por confirmar", options: [
    { value: "Por confirmar", label: "Por confirmar" }, { value: "Confirmada", label: "Confirmada" }, { value: "Cancelada", label: "Cancelada" },
  ] },
];
const reservacionSeed: Reservacion[] = [
  { id: "re1", amenidad: "Salón de eventos", solicitante: "Depto 402", fecha: "2026-06-13", horario: "18:00–23:00", estado: "Confirmada" },
  { id: "re2", amenidad: "Cancha de pádel", solicitante: "Casa 12", fecha: "2026-06-08", horario: "19:00–20:00", estado: "Por confirmar" },
  { id: "re3", amenidad: "Asador 2", solicitante: "Depto 510", fecha: "2026-06-14", horario: "14:00–18:00", estado: "Cancelada" },
];
export const ReservacionesSection = () => (
  <CrudSection<Reservacion> title="Reservaciones" subtitle="Reservas de amenidades y espacios"
    icon="ri-calendar-check-line" columns={reservacionColumns} fields={reservacionFields} seed={reservacionSeed} searchKey="solicitante" />
);

// --- Reglas y aforo --------------------------------------------------------
interface Regla extends Record<string, unknown> {
  id: string; amenidad: string; anticipacion: string; duracion: string; aforo: string; costo: string;
}
const reglaColumns: CrudColumn<Regla>[] = [
  { key: "amenidad", label: "Amenidad" },
  { key: "anticipacion", label: "Anticipación" },
  { key: "duracion", label: "Duración máx." },
  { key: "aforo", label: "Aforo", align: "right" },
  { key: "costo", label: "Costo", align: "right" },
];
const reglaFields: CrudField[] = [
  { key: "amenidad", label: "Amenidad", required: true, placeholder: "Salón de eventos" },
  { key: "anticipacion", label: "Anticipación", required: true, placeholder: "Hasta 30 días" },
  { key: "duracion", label: "Duración máxima", required: true, placeholder: "5 h" },
  { key: "aforo", label: "Aforo", type: "number", defaultValue: 0 },
  { key: "costo", label: "Costo", placeholder: "$900" },
];
const reglaSeed: Regla[] = [
  { id: "rg1", amenidad: "Salón de eventos", anticipacion: "30 días", duracion: "5 h", aforo: "80", costo: "$900" },
  { id: "rg2", amenidad: "Cancha de pádel", anticipacion: "7 días", duracion: "2 h", aforo: "4", costo: "$0" },
  { id: "rg3", amenidad: "Asador 2", anticipacion: "14 días", duracion: "4 h", aforo: "12", costo: "$300" },
];
export const ReglasSection = () => (
  <CrudSection<Regla> title="Reglas y aforo" subtitle="Reglas de uso por amenidad"
    icon="ri-list-settings-line" columns={reglaColumns} fields={reglaFields} seed={reglaSeed} searchKey="amenidad" />
);

// --- Recordatorios (list) --------------------------------------------------
interface Recordatorio extends Record<string, unknown> {
  id: string; reservacion: string; canal: string; cuando: string; estado: string;
}
const recordatorioColumns: CrudColumn<Recordatorio>[] = [
  { key: "reservacion", label: "Reservación" },
  { key: "canal", label: "Canal" },
  { key: "cuando", label: "Envío" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const recordatorioSeed: Recordatorio[] = [
  { id: "rc1", reservacion: "Salón — Depto 402", canal: "Push", cuando: "1 día antes", estado: "Enviado" },
  { id: "rc2", reservacion: "Pádel — Casa 12", canal: "Push", cuando: "2 h antes", estado: "Pendiente" },
  { id: "rc3", reservacion: "Asador — Depto 510", canal: "Email", cuando: "1 día antes", estado: "Pendiente" },
];
export const RecordatoriosSection = () => (
  <CrudSection<Recordatorio> title="Recordatorios" subtitle="Confirmaciones y recordatorios automáticos"
    icon="ri-notification-badge-line" columns={recordatorioColumns} fields={[]} seed={recordatorioSeed} searchKey="reservacion" readOnly />
);
